import React, { useState } from 'react';
import { AlertTriangle, X, Archive, RotateCcw } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import { useAppSelector } from '../../store/hooks';

interface ActionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  action: 'cancel' | 'archive' | 'restore';
  eventTitle: string;
  loading?: boolean;
}

const ActionConfirmationModal: React.FC<ActionConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  eventTitle,
  loading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (action === 'cancel' && !reason.trim()) {
      return; // Don't allow empty reason for cancel
    }
    onConfirm(action === 'cancel' ? reason : undefined);
  };

  const handleClose = () => {
    setReason(''); // Clear reason when closing
    onClose();
  };

  const getActionConfig = () => {
    switch (action) {
      case 'cancel':
        return {
          title: 'Cancel Event',
          icon: X,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          description: 'This will permanently cancel the event and notify all guests.',
          confirmText: 'Yes, Cancel Event',
          confirmVariant: 'danger' as const,
          warning: 'This action cannot be undone.'
        };
      case 'archive':
        return {
          title: 'Archive Event',
          icon: Archive,
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          description: 'This will archive the event and hide it from active event lists.',
          confirmText: 'Yes, Archive Event',
          confirmVariant: 'outline' as const,
          warning: 'You can restore the event later if needed.'
        };
      case 'restore':
        return {
          title: 'Restore Event',
          icon: RotateCcw,
          iconColor: 'text-green-500',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          description: 'This will restore the event from archive and make it active again.',
          confirmText: 'Yes, Restore Event',
          confirmVariant: 'primary' as const,
          warning: 'The event will become visible to guests again.'
        };
      default:
        return {
          title: 'Confirm Action',
          icon: AlertTriangle,
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          description: 'Are you sure you want to perform this action?',
          confirmText: 'Yes, Continue',
          confirmVariant: 'primary' as const,
          warning: 'Please review your action carefully.'
        };
    }
  };

  const config = getActionConfig();
  const Icon = config.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      size="sm"
    >
      <div className="p-6">
        {/* Icon and Event Title */}
        <div className={`flex items-start p-4 rounded-lg border ${config.bgColor} ${config.borderColor} mb-6`}>
          <Icon className={`h-6 w-6 mr-3 mt-0.5 ${config.iconColor}`} />
          <div className="flex-1">
            <h3 className={`text-lg font-semibold mb-2 ${config.textColor}`}>
              {config.title}
            </h3>
            <p className={`text-sm ${config.textColor} opacity-80`}>
              <strong>Event:</strong> {eventTitle}
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-3`}>
            {config.description}
          </p>
          
          {/* Reason Input for Cancel Action */}
          {action === 'cancel' && (
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Reason for cancellation *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for canceling this event..."
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  theme === 'dark' 
                    ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400' 
                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                }`}
                required
              />
              {!reason.trim() && (
                <p className="mt-1 text-sm text-red-500">Please provide a reason for cancellation</p>
              )}
            </div>
          )}
          
          <div className={`p-3 rounded-md ${theme === 'dark' ? 'bg-yellow-900/20 border border-yellow-700/50' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-start">
              <AlertTriangle className={`h-4 w-4 mr-2 mt-0.5 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <p className={`text-xs ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                {config.warning}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={config.confirmVariant}
            onClick={handleConfirm}
            disabled={loading || (action === 'cancel' && !reason.trim())}
            className="min-w-[120px]"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              config.confirmText
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ActionConfirmationModal;
